/** "Revert change set…" on the AI vocabulary tab: the dry run of reverting one of the tenant's
 *  vocabulary change sets, the records to leave as they are, a reason — then the revert, now.
 *
 *  Like the AI changes page's dialog (whose plan view it shows), each change of the skips asks
 *  for a new plan, Revert waits for the plan on screen and carries its digest, and the plan's
 *  `blockers` decide what still needs a decision. Unlike it, the revert runs in the request (a
 *  vocabulary change set holds at most 50 entries) and is **never forced**: overwriting later
 *  edits stays the AI changes page's (`console_ai_changes_revert`, Ops Admin). A 409
 *  `stale_plan` / `revert_blocked` (nothing written) reads the plan again; `revert_refused`,
 *  `revert_failed` (rolled back) and `change_sets_disabled` say why. */
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { aiChangeKeys, revertIssue, shortId } from '@/api/aiChanges';
import * as vocabularyApi from '@/api/aiVocabulary';
import { VOCABULARY_LIMITS, vocabularyErrorOf, vocabularyKeys } from '@/api/aiVocabulary';
import { ConsoleApiError } from '@/api/client';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { aiChangeHref } from '@/features/ai-changes/aiChangeLinks';
import { BLOCKED_TEXT, PlanView, STALE_PLAN_TEXT } from '@/features/ai-changes/RevertChangeSetDialog';
import { usePermissions } from '@/hooks/usePermissions';

export function VocabularyRevertDialog({
  topId,
  changeSetId,
  onClose,
}: {
  topId: number;
  changeSetId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canOpenChanges = hasPerm('console_ai_changes_view');

  const [skips, setSkips] = useState<number[]>([]);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<{ severity: 'warning' | 'error'; text: string } | null>(
    null,
  );
  // A second click lands before the disabled re-render does (lessons.md, 2026-10-05).
  const submitting = useRef(false);

  const planQuery = useQuery({
    queryKey: vocabularyKeys.revertPlan(topId, changeSetId, skips),
    queryFn: () => vocabularyApi.revertPlan(topId, changeSetId, skips),
    placeholderData: keepPreviousData,
    // A plan describes the entries as they are now: never serve a remembered one.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const plan = planQuery.data;
  const planCurrent = Boolean(plan) && !planQuery.isPlaceholderData && !planQuery.isFetching;
  const planRefusal =
    planQuery.error instanceof ConsoleApiError ? vocabularyErrorOf(planQuery.error.body) : null;

  const mutation = useMutation({
    mutationFn: (digest: string) =>
      vocabularyApi.revert(topId, changeSetId, {
        reason: reason.trim(),
        expected_digest: digest,
        skips: [...skips].sort((a, b) => a - b),
      }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: vocabularyKeys.tab(topId) });
      void qc.invalidateQueries({ queryKey: aiChangeKeys.all });
      snackbar.show(`${result.message} The revert is a change set of its own.`, 'success', {
        autoHideDuration: 15_000,
        ...(canOpenChanges
          ? {
              action: {
                label: 'Open the revert',
                onClick: () => navigate(aiChangeHref(result.revert_change_set)),
              },
            }
          : {}),
      });
      onClose();
    },
    onError: (error) => {
      const refusal = error instanceof ConsoleApiError ? vocabularyErrorOf(error.body) : null;
      if (refusal?.code === 'stale_plan' || refusal?.code === 'revert_blocked') {
        // Nothing was written: read the plan again for these decisions.
        setNotice({
          severity: 'warning',
          text: refusal.code === 'stale_plan' ? STALE_PLAN_TEXT : BLOCKED_TEXT,
        });
        void qc.invalidateQueries({ queryKey: vocabularyKeys.revertPlan(topId, changeSetId, skips) });
        return;
      }
      if (refusal) {
        setNotice({ severity: 'error', text: refusal.detail });
        void qc.invalidateQueries({ queryKey: vocabularyKeys.tab(topId) });
        return;
      }
      setNotice({
        severity: 'error',
        text:
          error instanceof ConsoleApiError && error.status === 403
            ? 'You may not revert vocabulary changes (it needs console_ai_vocabulary_manage).'
            : `The revert didn’t run: ${error instanceof Error ? error.message : 'unknown error'}`,
      });
    },
    onSettled: () => {
      submitting.current = false;
    },
  });

  const running = mutation.isPending;
  const blockers = plan?.blockers ?? [];
  const reasonOk = reason.trim().length >= VOCABULARY_LIMITS.reasonMin;
  const blockReason = running
    ? 'Reverting…'
    : !plan
      ? null
      : !plan.enabled
        ? 'Change sets are not enabled yet: the plan reads, but a revert cannot run.'
        : !planCurrent
          ? 'Updating the plan…'
          : blockers.length > 0
            ? `${blockers.length} ${blockers.length === 1 ? 'entry still needs' : 'entries still need'} a decision.`
            : !reasonOk
              ? 'Enter a reason to revert.'
              : null;
  const canRevert = Boolean(plan) && planCurrent && blockReason === null;

  const toggleSkip = (seq: number) => {
    setNotice(null);
    setSkips((xs) => (xs.includes(seq) ? xs.filter((x) => x !== seq) : [...xs, seq]));
  };
  const submit = () => {
    if (!plan || !canRevert || submitting.current) return;
    submitting.current = true;
    setNotice(null);
    mutation.mutate(plan.digest);
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      size="md"
      disableBackdropClose
      closeDisabled={running}
      title={`Revert change set ${shortId(changeSetId)}`}
      actions={
        <>
          {blockReason && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 'auto', pl: 1 }}
              data-revert-block-reason
            >
              {blockReason}
            </Typography>
          )}
          <Button onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" disabled={!canRevert || running} onClick={submit}>
            {running ? 'Reverting…' : 'Revert'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Reverting puts the entries this change set wrote back as they were — now, as a change set of
        its own (revert the revert to redo). It is never forced: an entry changed again since stops
        here as a conflict
        {canOpenChanges ? (
          <>
            {' '}
            — overwriting it is done on the{' '}
            <Link component={RouterLink} to={aiChangeHref(changeSetId)}>
              AI change’s page
            </Link>
          </>
        ) : null}
        . Answers the AI gave meanwhile are not recalled.
      </Typography>

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 1.5 }} data-revert-notice>
          {notice.text}
        </Alert>
      )}

      {planQuery.isPending ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Planning the revert…
          </Typography>
        </Box>
      ) : planQuery.isError && !plan ? (
        <Alert
          severity="error"
          action={
            planRefusal ? undefined : (
              <Button color="inherit" size="small" onClick={() => void planQuery.refetch()}>
                Retry
              </Button>
            )
          }
        >
          {planRefusal
            ? planRefusal.detail
            : `Couldn’t plan the revert: ${planQuery.error instanceof Error ? planQuery.error.message : 'unknown error'}`}
        </Alert>
      ) : plan ? (
        <PlanView plan={plan} refreshing={!planCurrent} busy={running} onToggleSkip={toggleSkip} />
      ) : null}

      {plan && blockers.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-revert-blockers>
          <Typography variant="body2" fontWeight={600}>
            Still needs a decision
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {blockers.map((b) => (
              <li key={`${b.seq}:${b.code}`}>
                <Typography variant="body2">
                  #{b.seq} · {revertIssue(b.code).label}: {b.message}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {plan && (
        <TextField
          label="Reason"
          required
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={reason}
          disabled={running}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: VOCABULARY_LIMITS.reasonMax }}
          helperText="Required (at least 3 characters) — becomes the reason of the revert change set."
          sx={{ mt: 2 }}
        />
      )}
    </AppDialog>
  );
}
